import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import api, { formatError } from "@/api";
import { toast } from "sonner";
import { Landmark, Lock, ShieldCheck, Clock, Loader2 } from "lucide-react";

const STATUS_BADGE = {
  pending: { label: "Pending Verification", cls: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock },
  verified: { label: "Verified", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: ShieldCheck },
  locked: { label: "Locked by SuperAdmin", cls: "bg-slate-900 text-white border-slate-700", icon: Lock },
};

/**
 * 🏦 Bank & Payout Settings — clinic-side form. Read-only when
 * status='verified' or 'locked'. Fields editable only in 'pending'.
 */
export default function BankPayoutSettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    account_holder: "", bank_name: "", account_number: "", ifsc: "",
    default_consultation_fee: "",
  });

  const load = async () => {
    try {
      const { data } = await api.get("/org/me/bank");
      setData(data);
      setForm({
        account_holder: data.account_holder || "",
        bank_name: data.bank_name || "",
        account_number: "",
        ifsc: data.ifsc || "",
        default_consultation_fee: data.default_consultation_fee ?? "",
      });
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const status = data?.verification_status || "pending";
  const locked = status === "verified" || status === "locked";
  const badge = STATUS_BADGE[status];
  const BadgeIcon = badge.icon;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v === "" || v === null || v === undefined) return;
        payload[k] = k === "default_consultation_fee" ? Number(v) : v;
      });
      if (Object.keys(payload).length === 0) {
        toast.info("Nothing to save"); setSaving(false); return;
      }
      await api.put("/org/me/bank", payload);
      toast.success("Bank details saved");
      await load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <Card className="medical-card p-6" data-testid="bank-payout-settings">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-slate-800 to-slate-600 flex items-center justify-center shrink-0 shadow-lg shadow-slate-700/30">
          <Landmark className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-lg font-semibold text-slate-900">🏦 Bank &amp; Payout Settings</h3>
            <Badge className={`${badge.cls} border gap-1`} data-testid="bank-status-badge">
              <BadgeIcon className="w-3 h-3" /> {badge.label}
            </Badge>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Daily automated payouts run @ 8:00 PM IST. Once SuperAdmin verifies your details, these fields become read-only.
          </p>
        </div>
      </div>

      <div className="mt-5 grid md:grid-cols-2 gap-4">
        <Field label="Account Holder Name" required>
          <Input value={form.account_holder}
            onChange={(e) => setForm({ ...form, account_holder: e.target.value })}
            disabled={locked} data-testid="bank-holder-input"
            placeholder="e.g. City Hospital LLP" />
        </Field>
        <Field label="Bank Name" required>
          <Input value={form.bank_name}
            onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
            disabled={locked} data-testid="bank-name-input"
            placeholder="e.g. HDFC Bank" />
        </Field>
        <Field label="Account Number" required
               hint={data?.account_number_masked ? `Current: ${data.account_number_masked}` : "Not set yet"}>
          <Input value={form.account_number} type="password"
            onChange={(e) => setForm({ ...form, account_number: e.target.value.replace(/\s+/g, "") })}
            disabled={locked} data-testid="bank-acct-input"
            placeholder={locked ? "Locked" : "Enter to update"} />
        </Field>
        <Field label="IFSC Code" required hint="Format: ABCD0123456">
          <Input value={form.ifsc}
            onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase().slice(0, 11) })}
            disabled={locked} data-testid="bank-ifsc-input"
            placeholder="HDFC0001234" maxLength={11} />
        </Field>
        <Field label="Default Consultation Fee (₹)" hint="Applies to new doctors; existing doctors keep their own">
          <Input type="number" min="0" value={form.default_consultation_fee}
            onChange={(e) => setForm({ ...form, default_consultation_fee: e.target.value })}
            disabled={locked} data-testid="bank-default-fee-input" placeholder="500" />
        </Field>
      </div>

      {locked ? (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 text-slate-600 text-xs p-3 flex items-start gap-2" data-testid="bank-locked-notice">
          <Lock className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Your details are {status}. Contact SuperAdmin at support@upchaarq.in to request an edit.</span>
        </div>
      ) : (
        <div className="mt-4 flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="bank-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit for verification"}
          </Button>
        </div>
      )}
    </Card>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div>
      <Label className="text-xs text-slate-600 mb-1.5 block">
        {label} {required && <span className="text-rose-500">*</span>}
      </Label>
      {children}
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}
